import type { RealtimeChannel } from "ably";
import * as Ably from "ably";
import { ABLY_CONFIG, getRuntimeConfig } from "./config";
import { getAblyToxiproxyOverrides } from "./toxiproxy";

let _client: Ably.Realtime | null = null;
let _clientOptions: InitializeClientOptions | null = null;
let _clientConsumers = 0;

export type InitializeClientOptions = {
	key: string;
	clientId?: string;
};

export function initializeClient(options: InitializeClientOptions): Ably.Realtime {
	if (_client) {
		const hasSameOptions = _clientOptions?.key === options.key && _clientOptions?.clientId === options.clientId;

		if (!hasSameOptions) {
			throw new Error(
				`Ably client already initialized with a different configuration (existing clientId: ${_clientOptions?.clientId || "none"}, requested clientId: ${options.clientId || "none"}).`,
			);
		}

		_clientConsumers += 1;
		return _client;
	}

	const logger = getRuntimeConfig().logger;
	const toxiproxyOverrides = getAblyToxiproxyOverrides(options.clientId);

	if (Object.keys(toxiproxyOverrides).length > 0) {
		console.log("[Ably Toxiproxy] Overrides applied:", JSON.stringify(toxiproxyOverrides));
	} else {
		console.log("[Ably Toxiproxy] No overrides (disabled or clientId not matched)", {
			clientId: options.clientId,
		});
	}

	_client = new Ably.Realtime({
		key: options.key,
		clientId: options.clientId,
		...toxiproxyOverrides,
		transportParams: { remainPresentFor: 0 },
		disconnectedRetryTimeout: ABLY_CONFIG.disconnectedRetryTimeout,
		suspendedRetryTimeout: ABLY_CONFIG.suspendedRetryTimeout,
		recover: (lastConnectionDetails, cb) => {
			logger.info("FLOW", "Ably connection recovery attempted", lastConnectionDetails);
			cb(true);
		},
	});
	_clientOptions = options;
	_clientConsumers = 1;

	return _client;
}

export function getClient(): Ably.Realtime {
	if (!_client) {
		throw new Error("Ably client not initialized. Call initializeClient() first.");
	}
	return _client;
}

export function getChannel(channelName: string): RealtimeChannel {
	return getClient().channels.get(channelName);
}

export function destroyClient(): void {
	if (_client) {
		_clientConsumers = Math.max(0, _clientConsumers - 1);

		if (_clientConsumers > 0) {
			return;
		}

		_client.close();
		_client = null;
		_clientOptions = null;
	}
}
